<template>
  <div class="contact-page">
    <AppHead />

    <main class="contact-page__main">
      <div class="contact-page__container">

        <!-- Progress Steps -->
        <div class="contact-steps">
          <template v-for="s in [1, 2, 3]" :key="s">
            <div class="contact-steps__segment">
              <div
                class="contact-steps__dot"
                :class="{
                  'is-future': step < s && step !== 4,
                  'is-active': step === s && step !== 4,
                  'is-complete': step > s || step === 4,
                }"
              >
                <span v-if="step < s && step !== 4">{{ s }}</span>
                <span v-else-if="step === s && step !== 4">{{ s }}</span>
                <CheckIcon v-else class="contact-steps__check" />
              </div>
              <div
                v-if="s < 3"
                class="contact-steps__bar"
                :class="{ 'is-done': step > s || step === 4 }"
              />
            </div>
          </template>
        </div>

        <div class="contact-card">
          <!-- Step 1: Nature de la demande -->
          <div v-if="step === 1">
            <h2 class="contact-title">Nature de votre demande</h2>
            <div class="contact-stack">
              <button
                v-for="opt in demandeOptions"
                :key="opt.value"
                type="button"
                class="contact-choice"
                :class="{ 'is-selected': formData.demande === opt.value }"
                @click="selectDemande(opt.value)"
              >
                <div class="contact-choice__row">
                  <div>
                    <h3 class="contact-choice__title">{{ opt.label }}</h3>
                    <p v-if="opt.helper" class="contact-choice__helper">{{ opt.helper }}</p>
                  </div>
                  <div class="contact-choice__chev">→</div>
                </div>
              </button>
            </div>
          </div>

          <!-- Step 2: Message optionnel -->
          <div v-if="step === 2">
            <h2 class="contact-title">Votre message (optionnel)</h2>
            <p class="contact-lead">Décrivez brièvement votre besoin (si vous le souhaitez).</p>

            <textarea
              v-model="formData.message"
              rows="5"
              class="contact-input contact-input--textarea"
              placeholder="Votre message..."
            />

            <div class="contact-actions">
              <button
                type="button"
                class="contact-btn contact-btn--ghost"
                @click="step = 1"
              >
                Retour
              </button>
              <button
                type="button"
                class="contact-btn contact-btn--primary"
                @click="step = 3"
              >
                Continuer
              </button>
            </div>
          </div>

          <!-- Step 3: Contact Info -->
          <div v-if="step === 3">
            <h2 class="contact-title">Vos informations</h2>

            <div
              v-if="submitError"
              class="notification error mb-6"
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
                />
              </svg>
              <span>{{ submitError }}</span>
            </div>

            <form class="contact-form" @submit.prevent="requestMeeting">
              <div class="sr-only" aria-hidden="true">
                <label for="contact-website">Laissez ce champ vide</label>
                <input
                  id="contact-website"
                  v-model="formData.website"
                  type="text"
                  name="website"
                  tabindex="-1"
                  autocomplete="off"
                />
              </div>
              <div>
                <label class="contact-label">Nom complet</label>
                <input
                  v-model="formData.nom"
                  type="text"
                  required
                  class="contact-input"
                  placeholder="Jean Dupont"
                />
              </div>
              <div>
                <label class="contact-label">Email</label>
                <input
                  v-model="formData.email"
                  type="email"
                  required
                  class="contact-input"
                  placeholder="jean@exemple.com"
                />
              </div>
              <div>
                <label class="contact-label">Téléphone</label>
                <input
                  v-model="formData.telephone"
                  type="tel"
                  class="contact-input"
                  placeholder="+225 07 12 00 12 00"
                />
              </div>

              <div class="contact-recap">
                <h3 class="contact-recap__title">Récapitulatif</h3>
                <div class="contact-recap__body">
                  <p><strong>Demande :</strong> {{ demandeLabel }}</p>
                  <p><strong>Message :</strong> {{ formData.message ? formData.message : '—' }}</p>
                </div>
              </div>

              <div v-if="captchaProvider === 'turnstile'" class="contact-turnstile">
                <div ref="turnstileContainerRef" class="turnstile-widget" />
              </div>

              <div class="contact-actions">
                <button
                  type="button"
                  class="contact-btn contact-btn--ghost"
                  :disabled="submitting"
                  @click="step = 2"
                >
                  Retour
                </button>
                <button
                  type="submit"
                  class="contact-btn contact-btn--primary contact-btn--wide"
                  :disabled="submitting || (captchaProvider === 'turnstile' && !turnstileToken)"
                >
                  {{ submitting ? 'Envoi…' : 'Envoyer' }}
                </button>
              </div>
            </form>
          </div>

          <!-- Step 4: Confirmation -->
          <div v-if="step === 4" class="contact-success">
            <div class="contact-success__icon-wrap" aria-hidden="true">
              <CheckIcon class="contact-success__icon" />
            </div>
            <h2 class="contact-success__title">Message envoyé</h2>
            <p class="contact-success__text">Merci. Notre équipe vous recontactera sous 24 heures.</p>
          </div>
        </div>
      </div>
    </main>

    <footer>
      <SiteFooter />
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { getErrorMessage } from '@/utils/errorMessages';
import { createSubmitGuard } from '@/utils/submitGuard';
import { trackGenerateLead } from '@/utils/analytics';
import { CheckIcon } from '@heroicons/vue/24/outline';
import AppHead from '@/components/public/AppHead.vue';
import SiteFooter from '@/components/public/Footer.vue';

function readRuntimeTurnstileSiteKey() {
  try {
    const g = window.__TURNSTILE_SITE_KEY__;
    if (g && String(g).trim()) return String(g).trim();
    const el = document.querySelector('meta[name="turnstile-site-key"]');
    const content = el?.getAttribute('content');
    return content && String(content).trim() ? String(content).trim() : '';
  } catch {
    return '';
  }
}

const route = useRoute();
const step = ref(1);
const submitting = ref(false);
const submitError = ref('');
const contactSubmitGuard = createSubmitGuard(4000);
const turnstileToken = ref('');
const turnstileContainerRef = ref(null);
const turnstileWidgetId = ref(null);
const turnstileSiteKey = ref(
  String(process.env.VUE_APP_TURNSTILE_SITE_KEY || '').trim() || readRuntimeTurnstileSiteKey()
);
const captchaProvider = computed(() => (turnstileSiteKey.value ? 'turnstile' : 'none'));

const demandeOptions = [
  { value: 'diagnostic', label: 'Diagnostic patrimonial' },
  { value: 'constitution', label: 'Constitution de patrimoine' },
  { value: 'retraite', label: 'Préparation retraite' },
  { value: 'transmission', label: 'Transmission patrimoniale' },
  { value: 'autre', label: 'Autre demande' },
];

const formData = ref({
  demande: '',
  message: '',
  nom: '',
  email: '',
  telephone: '',
  website: '',
});

const demandeLabel = computed(() => {
  const hit = demandeOptions.find((o) => o.value === formData.value.demande);
  return hit?.label || '—';
});

function selectDemande(value) {
  formData.value.demande = value;
  step.value = 2;
}

onMounted(() => {
  const q = String(route.query.demande || '').trim();
  if (q && demandeOptions.some((o) => o.value === q)) {
    selectDemande(q);
  }
});

async function requestMeeting() {
  const guarded = await contactSubmitGuard.run(async () => {
    await executeContactSubmit();
  });
  if (!guarded.ok && (guarded.reason === 'in_flight' || guarded.reason === 'cooldown')) {
    submitError.value = 'Envoi en cours, veuillez patienter avant de réessayer.';
  }
}

async function executeContactSubmit() {
  submitError.value = '';
  submitting.value = true;
  try {
    const payload = {
      nom: formData.value.nom,
      email: formData.value.email,
      telephone: formData.value.telephone,
      demande: formData.value.demande,
      message: formData.value.message,
      website: formData.value.website,
    };
    if (captchaProvider.value === 'turnstile' && turnstileToken.value) {
      payload.turnstile_token = turnstileToken.value;
    }
    await axios.post('/api/contact-public/', payload);
    step.value = 4;
    trackGenerateLead({ demande: formData.value.demande });
  } catch (e) {
    submitError.value = getErrorMessage(e).message;
    if (captchaProvider.value === 'turnstile' && window.turnstile && turnstileWidgetId.value != null) {
      try { window.turnstile.reset(turnstileWidgetId.value); } catch (_) {}
    }
    turnstileToken.value = '';
  } finally {
    submitting.value = false;
  }
}

async function mountTurnstileWidget() {
  if (captchaProvider.value !== 'turnstile' || step.value !== 3) return;
  const siteKey = turnstileSiteKey.value;
  if (!siteKey) return;
  await nextTick();
  if (turnstileWidgetId.value != null) return;
  try {
    await new Promise((resolve, reject) => {
      if (window.turnstile) return resolve();
      const existing = document.querySelector('script[data-turnstile="1"]');
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', () => reject(new Error('turnstile_load_failed')), { once: true });
        return;
      }
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.defer = true;
      s.dataset.turnstile = '1';
      s.addEventListener('load', resolve, { once: true });
      s.addEventListener('error', () => reject(new Error('turnstile_load_failed')), { once: true });
      document.head.appendChild(s);
    });
    await nextTick();
    const el = turnstileContainerRef.value;
    if (!el || !window.turnstile) {
      submitError.value = 'Vérification anti-spam indisponible. Réessayez plus tard.';
      return;
    }
    turnstileWidgetId.value = window.turnstile.render(el, {
      sitekey: siteKey,
      callback: (token) => { turnstileToken.value = token || ''; },
      'error-callback': () => { turnstileToken.value = ''; },
      'expired-callback': () => { turnstileToken.value = ''; },
    });
  } catch {
    submitError.value = 'Vérification anti-spam indisponible. Désactivez les bloqueurs de scripts.';
  }
}

watch(step, (s) => {
  if (s === 3) mountTurnstileWidget();
});
</script>

<style scoped>
.contact-page {
  min-height: 100vh;
  font-family: var(--font-primary);
  color: var(--bank-gray-900);
  background:
    radial-gradient(1200px 600px at 20% 0%, rgba(212, 175, 55, 0.10), transparent 60%),
    radial-gradient(900px 500px at 90% 20%, rgba(10, 26, 47, 0.10), transparent 55%),
    linear-gradient(180deg, rgba(249, 250, 251, 1), rgba(249, 250, 251, 1));
}

.contact-page__main {
  padding: 6rem 1rem 4rem;
}

.contact-page__container {
  max-width: 56rem;
  margin: 0 auto;
}

.contact-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 3rem;
}

.contact-steps__segment {
  display: flex;
  align-items: center;
}

.contact-steps__dot {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  border: 1px solid rgba(10, 26, 47, 0.12);
  background: rgba(255, 255, 255, 0.85);
  color: rgba(10, 26, 47, 0.55);
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.contact-steps__dot.is-future {
  opacity: 0.75;
}

.contact-steps__dot.is-active {
  background: linear-gradient(135deg, rgba(10, 26, 47, 0.96), rgba(26, 34, 56, 0.96));
  color: #fff;
  border-color: rgba(212, 175, 55, 0.35);
  box-shadow: 0 14px 30px rgba(10, 26, 47, 0.18);
}

.contact-steps__dot.is-complete {
  background: rgba(45, 134, 89, 0.12);
  color: var(--bank-success);
  border-color: rgba(45, 134, 89, 0.22);
}

.contact-steps__check {
  width: 1.25rem;
  height: 1.25rem;
}

.contact-steps__bar {
  width: 5rem;
  height: 0.25rem;
  margin: 0 0.5rem;
  border-radius: 999px;
  background: rgba(10, 26, 47, 0.10);
  transition: background 0.2s ease;
}

.contact-steps__bar.is-done {
  background: linear-gradient(90deg, rgba(212, 175, 55, 0.2), rgba(212, 175, 55, 0.85), rgba(212, 175, 55, 0.2));
}

.contact-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(10, 26, 47, 0.12);
  border-radius: 1.25rem;
  box-shadow: 0 28px 70px rgba(10, 26, 47, 0.10);
  padding: 2rem;
}

.contact-title {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--heading-letter-tight);
  line-height: var(--line-tight);
  color: rgba(10, 26, 47, 0.92);
  margin: 0 0 1.25rem 0;
}

.contact-lead {
  margin: 0 0 1.25rem 0;
  color: var(--bank-gray-600);
  line-height: 1.6;
}

.contact-stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.contact-choice {
  width: 100%;
  text-align: left;
  padding: 1.25rem;
  border-radius: 0.875rem;
  border: 1px solid rgba(10, 26, 47, 0.12);
  background: rgba(255, 255, 255, 0.65);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
  cursor: pointer;
}

.contact-choice:hover {
  transform: translateY(-1px);
  border-color: rgba(212, 175, 55, 0.45);
  box-shadow: 0 18px 44px rgba(10, 26, 47, 0.08);
  background: rgba(212, 175, 55, 0.06);
}

.contact-choice.is-selected {
  border-color: rgba(212, 175, 55, 0.65);
  background: rgba(212, 175, 55, 0.10);
  box-shadow: 0 18px 44px rgba(10, 26, 47, 0.10);
}

.contact-choice__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.contact-choice__title {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-weight-semibold);
  color: rgba(10, 26, 47, 0.92);
}

.contact-choice__helper {
  margin: 0.35rem 0 0 0;
  color: var(--bank-gray-500);
  font-size: var(--text-sm);
}

.contact-choice__chev {
  font-size: var(--text-2xl);
  font-weight: var(--font-weight-bold);
  color: rgba(212, 175, 55, 0.95);
  line-height: 1;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.contact-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--muted);
  margin: 0 0 var(--space-2) 0;
}

.contact-input {
  width: 100%;
  box-sizing: border-box;
  min-height: 48px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-field);
  border: 1px solid var(--line);
  background: var(--paper);
  color: var(--brand-ink);
  outline: none;
  transition: border-color var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease);
  font-family: var(--font-body);
  font-size: 1rem;
}

.contact-input:focus {
  border-color: var(--brand-gold);
  box-shadow: 0 0 0 4px var(--brand-gold-soft);
}

.contact-input--textarea {
  resize: vertical;
  min-height: 140px;
  padding-top: var(--space-3);
  padding-bottom: var(--space-3);
  line-height: 1.55;
}

.contact-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
  margin-top: var(--space-2);
}

/* Boutons : styles globaux Lot 3 (components.css) */
.contact-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.contact-btn--wide {
  min-width: 12rem;
}

.contact-recap {
  border-radius: var(--radius-card);
  padding: var(--space-5);
  border: 1px solid var(--brand-gold-soft);
  background: var(--brand-gold-soft);
}

.contact-recap__title {
  margin: 0 0 0.75rem 0;
  font-size: var(--text-lg);
  font-weight: var(--font-weight-semibold);
  color: rgba(10, 26, 47, 0.92);
}

.contact-recap__body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: var(--bank-gray-600);
  line-height: 1.55;
}

.contact-turnstile {
  display: flex;
  justify-content: center;
  min-height: 68px;
}

.turnstile-widget {
  min-height: 65px;
}

.contact-success {
  text-align: center;
  padding: 2.5rem 0.5rem;
}

.contact-success__icon-wrap {
  width: 5rem;
  height: 5rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  background: rgba(45, 134, 89, 0.12);
  border: 1px solid rgba(45, 134, 89, 0.22);
}

.contact-success__icon {
  width: 2.5rem;
  height: 2.5rem;
  color: var(--bank-success);
}

.contact-success__title {
  margin: 0 0 0.75rem 0;
  font-family: var(--font-display);
  font-size: var(--text-3xl);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--heading-letter-display);
  line-height: var(--line-tight);
  color: rgba(10, 26, 47, 0.92);
}

.contact-success__text {
  margin: 0;
  font-size: var(--text-xl);
  color: var(--bank-gray-600);
  line-height: 1.55;
}

@media (max-width: 640px) {
  .contact-card {
    padding: 1.25rem;
  }

  .contact-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .contact-btn--wide {
    width: 100%;
  }
}

/* Aligné sur FormApat.vue : notifications accessibles (pas de manipulation DOM type document.getElementById). */
.contact-page .notification {
  padding: 15px;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-weight: 500;
  font-size: 0.95rem;
  line-height: 1.45;
}

.contact-page .notification svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 2px;
}

.contact-page .notification svg path {
  fill: currentColor;
}

.contact-page .notification.error {
  background: rgba(197, 48, 48, 0.08);
  color: var(--bank-danger);
  border: 1px solid rgba(197, 48, 48, 0.18);
  border-radius: 12px;
}
</style>
